<template>
  <environment-banner v-if="isNotProduction" :environment="environment" :branch="branch" />

  <div class="wrapper">
    <navbar>
      <template v-slot:logo>
        <router-link
          :to="{ name: 'admin.dashboard' }"
          class="logo text-center text-decoration-none"
          @click="handleLogoClick"
        >
          <BaseLogo item-class="logo-lg" alignment="horizontal" />
          <BaseLogo
            item-class="logo-sm"
            alignment="single"
            :height="24"
            :class="{ 'test-environment-style': isNotProduction }"
          />
        </router-link>
      </template>
      <template v-slot:items>
        <list :listItems="listItems" item-class="side-nav side-nav-light" :header="true" />
      </template>
    </navbar>
    <div class="content-page position-relative">
      <div class="content">
        <!-- Start Topbar Content-->
        <div class="navbar-custom px-1">
          <AdminTopbarContent />
        </div>
        <!-- Start Topbar Content-->
        <div class="container-fluid mx-0 dashboard-container-for-admin">
          <BaseBreadcrumb />
          <router-view></router-view>
        </div>
      </div>
      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-md-6">
              {{ $t('views.layouts.tarfin_company_mark', [new Date().getFullYear()]) }}
            </div>
            <div class="col-md-6">
              <div class="text-md-end footer-links d-none d-md-block">
                <a :href="$t('views.layouts.about_us_link')" target="_blank">{{ $t('views.layouts.about_us') }}</a>
                <a :href="$t('views.layouts.privacy_policy_link')" target="_blank">{{
                  $t('views.layouts.privacy_agreement')
                }}</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue'

import store from '@/store/store.js'

import BaseBreadcrumb from '@/components/BaseBreadcrumb.vue'
import BaseLogo from '@/components/BaseLogo.vue'
import EnvironmentBanner from '@/components/EnvironmentBanner/index.vue'
import List from '@/components/List.vue'
import Navbar from '@/components/Navbar.vue'
import AdminTopbarContent from '@/views/layouts/AdminTopbarContent.vue'

async function getUser() {
  await store.dispatch('auth/getUser').then(user => {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: `${user.firstname} ${user.lastname}`
    })
  })
}

export default {
  name: 'admin',
  components: {
    Navbar,
    AdminTopbarContent,
    List,
    EnvironmentBanner,
    BaseBreadcrumb,
    BaseLogo
  },
  data() {
    return {
      listItems: [
        {
          text: this.$t('views.layouts.dashboard'),
          icon: 'mdi mdi-view-dashboard',
          name: 'admin.dashboard',
          permission: 'view-dashboard'
        },
        {
          text: this.$t('views.layouts.approvals'),
          icon: 'mdi mdi-check-circle',
          name: 'admin.approvals.index',
          permission: 'view-any-approvals'
        },
        {
          text: this.$t('views.layouts.sales'),
          icon: 'mdi mdi-briefcase',
          name: 'admin.parent.orders.index',
          child: [
            {
              text: this.$t('views.layouts.sales'),
              name: 'admin.orders.index',
              permission: 'view-any-order'
            },
            {
              text: this.$t('views.layouts.phone_surveys'),
              name: 'admin.phone-surveys.index',
              permission: 'view-any-phone-survey'
            },
            {
              text: this.$t('views.layouts.payment_reminders'),
              name: 'admin.payment-reminders.index',
              permission: 'view-any-payment-reminder'
            },
            {
              text: this.$t('views.layouts.administrative_proceeding_calls'),
              name: 'admin.administrative-proceeding-calls.index',
              permission: 'view-any-administrative-proceeding-reminder'
            },
            {
              text: this.$t('views.layouts.confirmation_calls'),
              name: 'admin.delivery-reminders.index',
              permission: 'view-any-delivery-reminder'
            },
            {
              text: this.$t('views.layouts.incoming_payments'),
              name: 'admin.incoming-payments.index',
              permission: 'view-any-bank-payment',
              show: () => {
                return this.$country.is('tr')
              }
            }
          ]
        },
        {
          text: this.$t('views.layouts.document_management'),
          icon: 'mdi mdi-text-box-multiple',
          name: 'admin.orders.documents.upload',
          show: () => {
            return this.$country.is('tr')
          },
          child: [
            {
              text: this.$t('views.layouts.uploaded_documents'),
              name: 'admin.documents.index',
              permission: 'view-any-document'
            },
            {
              text: this.$t('views.layouts.upload_document'),
              name: 'admin.upload_documents',
              child: [
                {
                  text: this.$t('views.layouts.signed_sales_document_upload'),
                  name: 'admin.documents.signedPromissoryNote.index',
                  permission: 'create-document'
                },
                {
                  text: this.$t('views.layouts.cancelled_promissory_notes_upload'),
                  name: 'admin.documents.cancelledPromissoryNote.index',
                  permission: 'create-document'
                },
                {
                  text: this.$t('views.layouts.identity_upload'),
                  name: 'admin.documents.identity.index',
                  permission: 'create-document'
                }
              ]
            },
            {
              text: this.$t('views.layouts.create_promissory_note_control_list'),
              name: 'admin.documents.document-control-processes.upload',
              permission: 'view-document-process',
              show: () => {
                return this.$country.is('tr')
              }
            }
          ]
        },
        {
          text: this.$t('views.layouts.application_management'),
          icon: 'mdi mdi-cards',
          name: 'admin.parent.applicationManagement',
          child: [
            {
              text: this.$t('views.layouts.applications'),
              name: 'admin.applications.index',
              permission: 'view-any-application'
            },
            {
              text: this.$t('views.layouts.pre-application'),
              name: 'admin.mobile-contacts.index',
              permission: 'view-any-application'
            },
            {
              text: this.$t('views.layouts.pre_approved_limit_applications'),
              name: 'admin.pre-approved-limit-applications.index',
              permission: 'view-any-pre-approved-limit-application',
              show: () => {
                return this.$country.is('tr')
              }
            },
            {
              text: this.$t('views.layouts.bulk_farmer_limit_applications'),
              name: 'admin.bulk-farmer-limit-applications.index',
              permission: 'view-any-bulk-farmer-limit-application',
              show: () => {
                return this.$country.is('tr')
              }
            },
            {
              text: this.$t('views.layouts.applied_products'),
              name: 'admin.order-items.index',
              permission: 'view-any-order-item'
            },
            {
              text: this.$t('views.layouts.sales_channels'),
              name: 'admin.sales-channels.index',
              permission: 'view-any-sales-channel'
            },
            {
              text: this.$t('views.layouts.questions'),
              name: 'admin.questions.index',
              permission: 'view-any-question'
            },
            {
              text: this.$t('views.layouts.black_list'),
              name: 'admin.blacklists.index',
              permission: 'view-any-blacklist'
            }
          ]
        },
        {
          text: this.$t('views.layouts.retailers'),
          icon: 'mdi mdi-store',
          name: 'admin.parent.retailers.index',
          child: [
            {
              text: this.$t('views.layouts.retailer_management'),
              name: 'admin.retailers.index',
              permission: 'view-any-retailer'
            },
            {
              text: this.$t('views.layouts.retailer_groups'),
              name: 'admin.ranks.index',
              permission: 'view-any-rank'
            },
            {
              text: this.$t('views.layouts.rewards'),
              name: 'admin.rewards.index',
              permission: 'view-any-reward'
            }
          ]
        },
        {
          text: this.$t('views.layouts.suppliers'),
          icon: 'mdi mdi-handshake',
          name: 'admin.suppliers.index',
          permission: 'view-any-supplier',
          show: () => {
            return this.$country.is('tr')
          }
        },
        {
          text: this.$t('views.layouts.farmers'),
          icon: 'mdi mdi-tractor',
          name: 'admin.farmers.index',
          permission: 'view-any-farmer'
        },
        {
          text: this.$t('views.layouts.tarfin_mobile'),
          icon: 'mdi mdi-package-variant-closed',
          name: 'admin.retailers.tarfinMobil',
          child: [
            {
              text: this.$t('views.layouts.purchase_requests'),
              name: 'admin.purchase-requests.index',
              permission: 'view-any-purchase-request'
            },
            {
              text: this.$t('views.layouts.admin_price'),
              name: 'admin.product-prices.index',
              permission: 'view-any-product-price'
            },
            {
              text: this.$t('views.layouts.suggested_products'),
              name: 'admin.suggested-products.index',
              permission: 'view-any-suggested-product'
            }
          ],
          show: () => {
            return this.$country.is('tr')
          }
        },
        {
          text: this.$t('views.layouts.products'),
          icon: 'mdi mdi-package-variant-closed',
          name: 'admin.parent.products.index',
          child: [
            {
              text: this.$t('views.layouts.products'),
              name: 'admin.products.index',
              permission: 'view-any-product'
            },
            {
              text: this.$t('views.layouts.categories'),
              name: 'admin.categories.index',
              permission: 'view-any-category'
            },
            {
              text: this.$t('views.layouts.brands'),
              name: 'admin.brands.index',
              permission: 'view-any-brand'
            },
            {
              text: this.$t('views.layouts.crops'),
              name: 'admin.crops.index',
              permission: 'view-any-crops'
            },
            {
              text: this.$t('views.layouts.crop-price-indices'),
              name: 'admin.crop-price-indices.index',
              permission: 'view-any-crop-price-index',
              show: () => {
                return this.$country.is('tr')
              }
            },
            {
              text: this.$t('views.layouts.payment_periods'),
              name: 'admin.purchase-periods.index',
              permission: 'view-any-purchase-period'
            },
            {
              text: this.$t('views.layouts.product_market_price'),
              name: 'admin.product-market-prices.index',
              permission: 'view-any-product-price'
            }
          ]
        },
        {
          text: this.$t('views.layouts.stock_management'),
          icon: 'mdi mdi-truck',
          name: 'admin.parent.retailer-stock-inputs.index',
          show: () => {
            return this.$country.is('tr')
          },
          child: [
            {
              text: this.$t('views.layouts.stock_inputs'),
              name: 'admin.retailer-stock-inputs.index',
              permission: 'view-any-retailer-stock-input'
            },
            {
              text: this.$t('views.layouts.short_sales'),
              name: 'admin.retailer-short-sales.index',
              permission: 'view-any-retailer-short-sale'
            },
            {
              text: this.$t('views.layouts.tarfin_pro_quotations'),
              name: 'admin.tarfin-pro-quotations.index',
              permission: 'view-any-tarfin-pro-quotation'
            },
            {
              text: this.$t('views.layouts.tarfin_pro_product_price'),
              name: 'admin.tarfin-pro-product-price.index',
              permission: 'view-any-tarfin-pro-product-price',
              show: () => {
                return this.$country.isNot('ro')
              }
            },
            {
              text: this.$t('views.layouts.tarfin_pro_stock_interest'),
              name: 'admin.tarfin-pro-stock-interests.index',
              permission: 'view-any-tarfin-pro-stock-interest'
            },
            {
              text: this.$t('views.layouts.tarfin_pro_campaigns'),
              name: 'admin.tarfin-pro-campaigns.index',
              permission: 'view-any-tarfin-pro-stock-interest'
            }
            /*
            {
              text: 'Stok Çıkışları',
              name: 'admin.retailer-stock-outputs.index',
              permission: 'view-any-retailer-stock-output'
            }
            */
          ]
        },
        {
          text: this.$t('views.layouts.website'),
          icon: 'mdi mdi-web',
          name: 'admin.parent.contacts.index',
          child: [
            {
              text: this.$t('views.layouts.news'),
              name: 'admin.posts.index',
              permission: 'view-any-post'
            },
            {
              text: this.$t('views.layouts.job_boards'),
              name: 'admin.job-boards.index',
              permission: 'view-any-job-board',
              show: () => {
                return this.$country.is('tr')
              }
            },
            {
              text: this.$t('views.layouts.contacts'),
              name: 'admin.contacts.index',
              permission: 'view-any-contact'
            }
          ]
        },
        {
          text: this.$t('views.layouts.users'),
          icon: 'mdi mdi-account-multiple',
          name: 'admin.users.index',
          permission: 'view-any-user'
        },
        {
          text: this.$t('views.layouts.farm_runners'),
          icon: 'mdi mdi-gamepad-variant',
          name: 'admin.farm-runners.index'
        },
        {
          text: this.$t('views.layouts.finance'),
          name: 'admin.parent.finance',
          icon: 'mdi mdi-finance',
          child: [
            {
              text: this.$t('views.layouts.reports'),
              name: 'admin.reports',
              child: [
                {
                  text: this.$t('views.layouts.sales_reports'),
                  name: 'admin.reports.sales.index',
                  permission: 'view-sales-report'
                },
                {
                  text: this.$t('views.layouts.payment_performance'),
                  name: 'admin.reports.paymentPerformance.index',
                  permission: 'view-payment-performance-report'
                },
                {
                  text: this.$t('views.layouts.finance_portfolio_report'),
                  name: 'admin.reports.financePortfolio.index',
                  permission: 'view-finance-portfolio-report'
                },
                {
                  text: this.$t('views.layouts.underwriting_decision_report'),
                  name: 'admin.reports.underwritingDecision.index',
                  permission: 'view-underwriting-decision-report'
                },
                {
                  text: this.$t('views.layouts.mobile_price_report'),
                  name: 'admin.reports.mobilePrice.index',
                  permission: 'view-mobile-price-report'
                },
                {
                  text: this.$t('views.layouts.tmks_refund_report'),
                  name: 'admin.reports.tmksRefund.index',
                  permission: 'view-tmks-refund-report',
                  show: () => {
                    return this.$country.is('tr')
                  }
                }
              ]
            },
            {
              text: this.$t('views.layouts.interest_rate'),
              name: 'admin.interest_rates',
              child: [
                {
                  text: this.$t('views.layouts.base_interest_rate'),
                  name: 'admin.base-interest-rates.index',
                  permission: 'view-any-base-interest-rate'
                },
                {
                  text: this.$t('views.layouts.late_fee_interest_rate'),
                  name: 'admin.late-fee-interests.index',
                  permission: 'view-any-late-fee-interests'
                },
                {
                  text: this.$t('views.layouts.other_interest_rate'),
                  name: 'admin.interest-rates.index',
                  permission: 'view-any-interest-rate'
                }
              ]
            },
            {
              text: this.$t('views.layouts.financing_cost_summary'),
              name: 'admin.financing-cost-summary.index',
              permission: 'view-any-financing-cost-summary'
            },
            {
              text: this.$t('views.layouts.banks'),
              name: 'admin.banks.index',
              permission: 'view-any-bank'
            },
            {
              text: this.$t('views.layouts.holidays'),
              name: 'admin.holidays.index',
              permission: 'view-any-holiday'
            },
            {
              text: this.$t('views.layouts.service_errors'),
              name: 'admin.service-errors.index',
              permission: 'view-any-service-error'
            }
          ]
        },
        {
          text: this.$t('views.layouts.marketing'),
          icon: 'mdi mdi-target',
          name: 'admin.parent.marketing',
          child: [
            {
              text: this.$t('views.layouts.campaigns'),
              name: 'admin.campaigns.index',
              permission: 'view-any-campaign'
            },
            {
              text: this.$t('views.layouts.coupons'),
              name: 'admin.coupons.index',
              permission: 'view-any-coupon'
            },
            {
              text: this.$t('views.layouts.mobile_banner'),
              name: 'admin.mobile-banners.index',
              permission: 'view-any-mobile-banner'
            }
          ]
        },
        {
          text: this.$t('views.layouts.system'),
          icon: 'mdi mdi-application-outline',
          name: 'admin.parent.system',
          child: [
            {
              text: this.$t('views.layouts.roles'),
              name: 'admin.roles.index',
              permission: 'view-any-role'
            },
            {
              text: this.$t('views.layouts.permissions'),
              name: 'admin.permissions.index',
              permission: 'view-any-permission'
            },
            {
              text: this.$t('views.layouts.settings'),
              name: 'admin.settings.index',
              permission: 'view-any-config'
            },
            {
              text: this.$t('views.layouts.system_notifications'),
              name: 'admin.system-notifications.index',
              permission: 'view-any-system-notification'
            }
          ]
        }
      ],
      environment: 'dev',
      branch: ''
    }
  },
  computed: {
    isNotProduction() {
      return import.meta.env.MODE !== 'production'
    }
  },
  beforeRouteEnter(to, from, next) {
    store.commit('sideMenu/SET_ACTIVE_MENU_INDEX', to.name)

    getUser().then(() => {
      next()
    })
  },
  beforeRouteUpdate(to, from, next) {
    getUser().then(() => {
      next()
    })
  },
  methods: {
    toggleBodyClass(method, bodyClass) {
      const el = document.body

      if (method === 'add') {
        el.classList.add(bodyClass)
      } else {
        el.classList.remove(bodyClass)
      }
    },
    handleLogoClick() {
      store.commit('sideMenu/SET_ACTIVE_MENU_INDEX', 'admin.dashboard')
    },
    determineEnvironment(hostname) {
      if (import.meta.env.MODE === 'production') {
        return
      }

      if (import.meta.env.MODE === 'development') {
        this.environment = 'dev'
        document.querySelector('#favicon').href = '/images/environment/admin-favicon-develop.svg'
        return
      }

      if (hostname.includes('beykoz.tarfin.com') || hostname.includes('bucharest.tarfin.com')) {
        this.environment = 'staging'
        document.querySelector('#favicon').href = '/images/environment/admin-favicon-staging.svg'
        return
      }

      this.environment = 'dynamic'
      document.querySelector('#favicon').href = '/images/environment/admin-favicon-dynamic.svg'
    },
    extractDomainPrefix(hostname) {
      this.branch = hostname.split('.tarfin.com')[0]
    }
  },
  mounted() {
    let leftBarCompactMode = null
    if (localStorage.getItem('sidebarOpened') === '0') {
      leftBarCompactMode = 'condensed'
    } else if (localStorage.getItem('sidebarOpened') === '1') {
      leftBarCompactMode = 'scrollable'
    } else {
      leftBarCompactMode = 'scrollable'
    }

    document.body.setAttribute('data-leftbar-compact-mode', leftBarCompactMode)
    document.body.setAttribute('data-leftbar-theme', 'light')

    const hostname = window.location.hostname
    this.determineEnvironment(hostname)
    this.extractDomainPrefix(hostname)

    // this.toggleBodyClass('add', 'enlarged')
  },
  unmounted() {
    document.body.removeAttribute('data-leftbar-compact-mode')
    document.body.removeAttribute('data-leftbar-theme')
    // this.toggleBodyClass('remove', 'enlarged')
  }
}
</script>

<style scoped>
@media (min-width: 1025px) {
  .test-environment-style {
    margin-top: 20px;
  }
}
</style>
